/**
 * Коригування боргу / Взаємозалік — список (канон Норми #29, 2026-08-22):
 * .card + InfiniteDocGrid (серверна пагінація/сортування), фільтри рядком
 * під назвою — усі виконуються сервером (орг/контрагент/тип/період).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  debtAdjustments,
  type DebtAdjustmentListItem,
} from '@/api/debtAdjustments';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import SearchSelect from '@/components/SearchSelect';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TYPE_LABEL: Record<string, string> = {
  MUTUAL_OFFSET: 'Взаємозалік',
  TRANSFER: 'Перенос боргу',
  WRITE_OFF: 'Списання боргу',
  OFF_BALANCE_WRITEOFF: 'Списання з 071',
};

export default function DebtAdjustmentsPage() {
  const navigate = useNavigate();
  // Фільтри під назвою (шаблон проекту): орг СЛІДУЄ за шапкою до ручного
  // вибору (useOrgFilter), дати стартують з ВЕРХНЬОГО фільтра періоду
  // (зауваження власника 2026-08-19).
  const [orgId, setOrgId] = useOrgFilter();
  const { dateFrom: gFrom, dateTo: gTo } = useDefaultPeriod();
  const [fltContractor, setFltContractor] = useState('');
  const [fltType, setFltType] = useState('');
  const [fltFrom, setFltFrom] = useState(gFrom?.slice(0, 10) ?? '');
  const [fltTo, setFltTo] = useState(gTo?.slice(0, 10) ?? '');
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgList.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgList.data]);
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });

  const { datasource, total } = useInfiniteGridDatasource<DebtAdjustmentListItem>(
    (limit, offset, sort) => debtAdjustments.list({
      organization_id: orgId || undefined,
      contractor_id: fltContractor || undefined,
      adjustment_type: fltType || undefined,
      date_from: fltFrom || undefined,
      date_to: fltTo || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, fltContractor, fltType, fltFrom, fltTo],
    { attachmentsType: 'doc.debt_adjustments' },
  );

  const cols = useMemo<ColDef<DebtAdjustmentListItem>[]>(() => [
    {
      field: 'document_number', headerName: '№', width: 150,
      cellRenderer: (p: { data?: DebtAdjustmentListItem }) => p.data ? (
        <span>
          {p.data.external_source && (
            <span title="Імпортовано з 1С" style={{
              marginRight: 6, color: '#2563eb', border: '1px solid var(--badge-blue-bd)',
              borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px',
            }}>I</span>
          )}
          {p.data.document_number}
        </span>
      ) : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 170,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 190,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    {
      field: 'adjustment_type', headerName: 'Тип', width: 160,
      valueFormatter: (p) => p.value ? (TYPE_LABEL[p.value] ?? p.value) : '',
    },
    {
      field: 'contractor_name', headerName: 'Контрагент', flex: 1, minWidth: 180,
    },
    {
      field: 'total_amount', headerName: 'Сума заліку', width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => p.data ? (p.value ? '✓ так' : 'ні') : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Коригування боргу / Взаємозалік<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/debt-adjustments/new')}
        >
          + Новий документ
        </button>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 200, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <div style={{ width: 240 }}>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={fltContractor}
            onChange={setFltContractor}
            placeholder="контрагент (усі)"
          />
        </div>
        <select value={fltType} onChange={(e) => setFltType(e.target.value)}
          style={{ width: 170, flex: '0 0 auto' }}>
          <option value="">Усі типи</option>
          {Object.entries(TYPE_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltFrom} onChange={setFltFrom} />
        </div>
        <span style={{ color: 'var(--muted)' }}>—</span>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltTo} onChange={setFltTo} />
        </div>
      </div>
      <InfiniteDocGrid<DebtAdjustmentListItem>
        copyRow={(r) => navigate(`/debt-adjustments/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => debtAdjustments.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="debt-adjustments"
        onRowDoubleClicked={(r) => navigate(`/debt-adjustments/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
